import { useEffect, useRef, useState } from "react";
import { Layers, Plug, Rocket } from "lucide-react";

const STEPS = [
  {
    icon: Layers,
    step: "01",
    title: "Choose Your Service",
    desc: "Pick from Invoice OCR API, ScoreMyCV ATS CV Optimizer, PDF Splitter, Image Optimizer API, or Automated News Tracking — or use all five.",
    detail: "Instant signup · 10 bonus credits · No card required",
  },
  {
    icon: Plug,
    step: "02",
    title: "Integrate or Onboard",
    desc: "For OCR: integrate via REST API in minutes. For ScoreMyCV: sign up and scan your CV instantly. For tracking: our team handles setup.",
    detail: "REST API · JSON output · Full documentation",
  },
  {
    icon: Rocket,
    step: "03",
    title: "Automate & Scale",
    desc: "Let our platform run in the background while you focus on growing your business.",
    detail: "Pay as you go · Credits never expire · 99.9% uptime",
  },
];

export default function HowItWorks() {
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          intervalRef.current = setInterval(
            () => setActiveStep(s => (s + 1) % STEPS.length),
            2800
          );
        } else {
          clearInterval(intervalRef.current);
        }
      },
      { threshold: 0.2 }
    );
    const el = sectionRef.current;
    if (el) observer.observe(el);
    return () => {
      el && observer.unobserve(el);
      clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="py-24 md:py-32 px-6 md:px-10 section-light"
      data-testid="how-it-works-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <span className="overline-tag block mb-3">How It Works</span>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
            Up and running in three steps.
          </h2>
          <p className="text-[#94A3B8] text-lg max-w-xl mx-auto leading-relaxed">
            From sign-up to automation in minutes — no complex setup required.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Steps */}
          <div className="relative">
            <div className="absolute left-[22px] top-4 bottom-4 w-px bg-white/[0.06]" />
            <div
              className="absolute left-[22px] top-4 w-px bg-[#38BDF8]/60 transition-all duration-500"
              style={{ height: `${(activeStep / (STEPS.length - 1)) * 100}%` }}
            />

            {STEPS.map((step, i) => {
              const Icon = step.icon;
              const isActive = i === activeStep;
              const isPast = i < activeStep;
              return (
                <button
                  key={step.step}
                  onClick={() => {
                    setActiveStep(i);
                    clearInterval(intervalRef.current);
                  }}
                  className="relative pl-14 pr-4 py-5 text-left w-full"
                  data-testid={`step-${i}`}
                >
                  <div className={`absolute left-[14px] top-1/2 -translate-y-1/2 w-[18px] h-[18px] rounded-full flex items-center justify-center transition-all duration-300 ${
                    isActive
                      ? "bg-[#38BDF8] shadow-[0_0_12px_rgba(56,189,248,0.6)]"
                      : isPast
                      ? "bg-[#38BDF8]/30 border-2 border-[#38BDF8]/50"
                      : "bg-[#0d1635] border-2 border-white/[0.12]"
                  }`}>
                    {isActive && <div className="w-2 h-2 rounded-full bg-[#080f23]" />}
                  </div>

                  <div className={`transition-all duration-300 ${isActive ? "opacity-100" : "opacity-45 hover:opacity-70"}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-[#64748B] font-mono">{step.step}</span>
                      <h3 className={`text-sm font-semibold ${isActive ? "text-white" : "text-[#94A3B8]"}`}>{step.title}</h3>
                    </div>
                    {isActive && (
                      <div className="mt-2 space-y-1.5">
                        <p className="text-[#94A3B8] text-sm leading-relaxed">{step.desc}</p>
                        <span className="text-xs text-[#38BDF8] font-mono">{step.detail}</span>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Terminal */}
          <div className="terminal-window" data-testid="pipeline-visual">
            <div className="terminal-header flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
              <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
              <div className="w-3 h-3 rounded-full bg-[#27C93F]" />
              <span className="font-mono text-xs text-[#64748B] ml-3">tyo_pipeline.log</span>
            </div>
            <div className="p-6 font-mono text-sm space-y-3 min-h-[220px]">
              {STEPS.map((step, i) => {
                const isActive = i === activeStep;
                const isPast = i < activeStep;
                return (
                  <div key={i} className={`flex items-start gap-3 transition-all duration-300 ${
                    isActive ? "opacity-100" : isPast ? "opacity-35" : "opacity-15"
                  }`}>
                    <span className={`shrink-0 mt-0.5 ${isPast ? "text-[#22C55E]" : isActive ? "text-[#38BDF8]" : "text-[#64748B]"}`}>
                      {isPast ? "✓" : isActive ? "▶" : "·"}
                    </span>
                    <div>
                      <span className={isPast ? "text-[#22C55E]" : isActive ? "text-white" : "text-[#64748B]"}>
                        [{step.step}] {step.title}
                      </span>
                      {isActive && (
                        <div className="text-[#94A3B8] text-xs mt-1">
                          Status: <span className="text-[#38BDF8]">ACTIVE</span>
                          <span className="cursor-blink" />
                        </div>
                      )}
                      {isPast && <div className="text-[#22C55E]/60 text-xs mt-0.5">Completed</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
